"use client";
import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
// Drawer modal removed - using fixed positioning drawer instead
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../../components/ui/select";
import axios from "axios";
import { API_BASE_URL } from "../../../config/api";
import { useSelector } from "react-redux";
import { useOverlayHistory } from "../../../hooks/useOverlayHistory";

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: (user: any) => void;
}

export const UserForm: React.FC<UserFormProps> = ({ open, onClose, onAdd }) => {
  const { authToken = "", id = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  useOverlayHistory(open, onClose, "user-form");

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    mobile: "",
    email: "",
    address: "",
    companyName: "",
    gst: "",
    website: "",
    role: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const str = (v: unknown) => (v == null ? "" : String(v));

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    const username = str(formData.username).trim();
    const mobile = str(formData.mobile).trim();
    const email = str(formData.email).trim();
    const password = str(formData.password);
    const address = str(formData.address).trim();
    const companyName = str(formData.companyName).trim();

    // Strict role validation - must be selected and not empty
    if (!formData.role || formData.role === "") {
      newErrors.role = "Role is required";
    }

    // Required field validations
    if (!username) newErrors.username = "Username is required";
    if (!mobile) newErrors.mobile = "Mobile number is required";

    // Email validation (optional; format only when provided)
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email format";
    }

    // Role-based validations - only check if role is selected
    if (formData.role === "dealer" || formData.role === "interior") {
      // Password validation
      if (!password.trim()) {
        newErrors.password = "Password is required";
      } else if (password.length < 4) {
        newErrors.password = "4 characters needed";
      }
    }

    // Address / company required for dealer & interior only — optional for end-user
    if ((formData.role === "dealer" || formData.role === "interior") && !address) {
      newErrors.address = "Address is required";
    }

    if ((formData.role === "dealer" || formData.role === "interior") && !companyName) {
      newErrors.companyName = "Company name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createUser = async (userData: any) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}account/${id}/user/create`,
        userData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Error creating user";
      throw new Error(errorMessage);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const newUser = await createUser(formData);
      onAdd(formData);
      resetForm();
      onClose();
    } catch (err: any) {
      const errorMsg = err.message || "Error creating user";
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      mobile: "",
      email: "",
      address: "",
      companyName: "",
      gst: "",
      website: "",
      role: "",
    });
    setErrors({});
  };

  const handleRoleChange = (val: string) => {
    setFormData({ ...formData, role: val });
    // Clear role error when role is selected, but keep other field errors
    const newErrors = { ...errors };
    delete newErrors.role;
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.role &&
      formData.role !== "" &&
      str(formData.username).trim() &&
      str(formData.mobile).trim()
    );
  };

  // Prevent body scroll when drawer is open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  return (
    <>
      {/* Drawer Overlay */}
      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70]" onClick={onClose} />
      )}

      {/* Drawer Container */}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-[75] transform transition-all duration-300 w-full md:w-[55vw] md:h-auto max-h-screen md:max-h-[95vh] rounded-t-2xl md:rounded-lg flex flex-col overflow-visible ${
          open ? "translate-y-0 md:scale-100 pointer-events-auto opacity-100 visible" : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none invisible md:visible"
        }`}
      >
        {/* Header */}
        <div className="bg-white px-6 py-5 border-0 sticky top-0 flex justify-between items-center rounded-t-2xl md:rounded-t-lg">
          <div>
            <h2 className="text-2xl font-bold text-black">➕ Create New User</h2>
            <p className="text-black-600 text-sm mt-1">Add a new user to the system</p>
          </div>
          <button
            onClick={onClose}
            className="text-black hover:text-purple-600 text-2xl"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4 px-6 flex-1 overflow-y-auto overflow-x-visible pointer-events-auto">

          {errors.submit && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3">
              <p className="text-red-700 font-semibold text-sm">{errors.submit}</p>
            </div>
          )}

          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-4">
            <div>
              <Label className="text-gray-900 font-bold">� Role *</Label>
              <Select value={formData.role} onValueChange={handleRoleChange}>
                <SelectTrigger className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                  errors.role ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}>
                  <SelectValue placeholder="Select Role" />
                </SelectTrigger>
                <SelectContent className="bg-white rounded-lg shadow-lg">
                  <SelectItem value="dealer" className="cursor-pointer">Dealer</SelectItem>
                  <SelectItem value="interior" className="cursor-pointer">Interior</SelectItem>
                  <SelectItem value="end-user" className="cursor-pointer">End User</SelectItem>
                </SelectContent>
              </Select>
              {errors.role && <p className="text-red-600 text-xs mt-1">{errors.role}</p>}
            </div>

            <div>
              <Label className="text-gray-900 font-bold">👤 Username *</Label>
              <Input
                value={formData.username}
                onChange={(e) => {
                  setFormData({ ...formData, username: e.target.value });
                  setErrors({ ...errors, username: "" });
                }}
                placeholder="Enter username"
                className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                  errors.username ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}
              />
              {errors.username && <p className="text-red-600 text-xs mt-1">{errors.username}</p>}
            </div>

            <div>
              <Label className="text-gray-900 font-bold">📱 Mobile *</Label>
              <Input
                type="tel"
                inputMode="numeric"
                value={formData.mobile}
                onChange={(e) => {
                  // Only allow numbers
                  const numbersOnly = e.target.value.replace(/[^0-9]/g, "");
                  setFormData({ ...formData, mobile: numbersOnly });
                  setErrors({ ...errors, mobile: "" });
                }}
                placeholder="10 digit number (without country code)"
                className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                  errors.mobile ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}
              />
              {errors.mobile && <p className="text-red-600 text-xs mt-1">{errors.mobile}</p>}
            </div>

            <div>
              <Label className="text-gray-900 font-bold">📧 Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  setErrors({ ...errors, email: "" });
                }}
                placeholder="Enter email address (optional)"
                className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                  errors.email ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}
              />
              {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>

          {/* Conditional Fields - Company Information */}
          {(formData.role === "dealer" || formData.role === "interior" || formData.role === "end-user") && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-4">
              <p className="text-sm font-bold text-gray-700">🏢 Company Information</p>
              
              <div>
                <Label className="text-gray-900 font-bold">
                  Company Name
                  {(formData.role === "dealer" || formData.role === "interior") ? " *" : " (optional)"}
                </Label>
                <Input
                  value={formData.companyName}
                  onChange={(e) => {
                    setFormData({ ...formData, companyName: e.target.value });
                    setErrors({ ...errors, companyName: "" });
                  }}
                  placeholder="Enter company name"
                  className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                    errors.companyName ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-blue-500"
                  }`}
                />
                {errors.companyName && <p className="text-red-600 text-xs mt-1">{errors.companyName}</p>}
              </div>

              <div>
                <Label className="text-gray-900 font-bold">
                  Address
                  {(formData.role === "dealer" || formData.role === "interior") ? " *" : " (optional)"}
                </Label>
                <Input
                  value={formData.address}
                  onChange={(e) => {
                    setFormData({ ...formData, address: e.target.value });
                    setErrors({ ...errors, address: "" });
                  }}
                  placeholder="Enter address"
                  className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                    errors.address ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-blue-500"
                  }`}
                />
                {errors.address && <p className="text-red-600 text-xs mt-1">{errors.address}</p>}
              </div>

              <div>
                <Label className="text-gray-900 font-bold">🏷️ GST Number</Label>
                <Input
                  value={formData.gst}
                  onChange={(e) => setFormData({ ...formData, gst: e.target.value })}
                  placeholder="Enter GST number"
                  className="mt-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 py-2.5"
                />
              </div>

              <div>
                <Label className="text-gray-900 font-bold">🌐 Website</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="Enter website URL"
                  className="mt-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 py-2.5"
                />
              </div>
            </div>
          )}

          {/* Password Field - Only for dealer and interior */}
          {(formData.role === "dealer" || formData.role === "interior") && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div>
                <Label className="text-gray-900 font-bold">🔐 Password *</Label>
                <div className="relative mt-2">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      setErrors({ ...errors, password: "" });
                    }}
                    placeholder="Enter password (min 4 characters)"
                    className={`border-2 rounded-lg focus:ring-0 py-2.5 pr-10 ${
                      errors.password ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-orange-500"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <p className="text-red-600 text-xs mt-1">{errors.password}</p>}
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 px-6 py-6 border-t bg-white rounded-b-2xl md:rounded-b-lg flex-shrink-0">
          <Button type="button" variant="outline" onClick={onClose} className="px-6 font-semibold border-2">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading || !isFormValid()}
            onClick={handleSubmit}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-6 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Creating..." : "Create User"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default UserForm;
